import React, { useEffect, useState } from "react";

import GroupsAndTeamsContextProvider from "../groupsAndTeams/context/GroupsAndTeamsContext";
import ReviewContextProvider from "./review/context/ReviewContext";
import GroupFormContextProvider from "../groupsAndTeams/configuration/context/GroupFormContext";
import { QuantityContextProvider } from "../root/contexts/QuantityContext";
import { ExcelUploadContextProvider } from "../root/contexts/ExcelUploadContext";

import { TeamDetails } from "./configuration/TeamDetails";
import TeamDetailsHeader from "./configuration/header/TeamDetailsHeader";
import { TeamDetailsFooter } from "./configuration/footer/TeamDetailsFooter";
import { useTracking } from "../root/contexts/TrackingContext";
import { useBffData, useMerchantAvailability } from "../../hooks/queries";
import { TrackingLabel } from "../../types/TrackingConfiguration";

import "./teams.scss";
import { WranglerDataProvider } from "@vp/wrangler-data-source";
import { useAppState } from "../root/contexts/AppContext";
import { getTenantConfig } from "../../utils/tenantHelpers";
import { ProductOptionsTranslations } from "@vp/product-options-ui";
import { usePricingContext } from "../root/contexts/PricingContext";
import { useChatAnywhere } from "../../hooks/useChatAnywhere";

const GroupsAndTeams: React.FC<{}> = () => {
  const { data: bffData, isFetched: bffDataIsFetched } = useBffData();
  const { isFetched: inventoryIsFetched } = useMerchantAvailability();
  const [isProductViewedTracked, setIsProductViewedTracked] = useState(false);
  const { isTrackingReady, trackProductViewed } = useTracking();

  const isLoading = !bffDataIsFetched || !inventoryIsFetched;

  useEffect(() => {
    if (isProductViewedTracked) return;

    if (isTrackingReady && !isLoading) {
      const { mpvId, productName, productKey, productVersion } = bffData!;
      trackProductViewed(
        TrackingLabel.TEAM_DETAILS_PAGE,
        mpvId,
        productName,
        productKey,
        productVersion,
      );
      setIsProductViewedTracked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrackingReady, isLoading]);

  return (
    <QuantityContextProvider>
      <GroupsAndTeamsContextProvider>
        <ExcelUploadContextProvider>
          <GroupFormContextProvider>
            <ReviewContextProvider>
              <TeamDetailsHeader />
              <TeamDetails />
              <TeamDetailsFooter />
            </ReviewContextProvider>
          </GroupFormContextProvider>
        </ExcelUploadContextProvider>
      </GroupsAndTeamsContextProvider>
    </QuantityContextProvider>
  );
};

const GroupsAndTeamsWrapper: React.FC = () => {
  const { data: bffData, isFetched: bffDataIsFetched } = useBffData();
  const { isPricingContextInitialized, pricingContextModule } =
    usePricingContext();
  const { culture } = useAppState();
  useChatAnywhere();
  if (!bffDataIsFetched || !isPricingContextInitialized) {
    return <></>;
  }

  if (bffData?.isColorOptionEnabled) {
    const colorOptionName = bffData!.colorOptionName!;
    const { [colorOptionName]: selectedColor, ...selectedOptions } =
      bffData!.lowestPriceAttributes;

    // WranglerDataProvider here is used for color option button text information
    return (
      <WranglerDataProvider
        merchant="Vistaprint"
        culture={culture}
        productKey={bffData.productKey}
        productVersion={bffData.productVersion}
        includeVat={pricingContextModule.vatInclusive}
        priceStyle="relative"
        selectedAttributes={{ [colorOptionName]: selectedColor }}
        initialSelectedAttributes={selectedOptions}
        excludeInitialSelectedAttributes={true}
        merchandisingExperience="Product Page"
        requestor={getTenantConfig().requestor}
      >
        <ProductOptionsTranslations locale={culture}>
          <GroupsAndTeams />
        </ProductOptionsTranslations>
      </WranglerDataProvider>
    );
  }
  return <GroupsAndTeams />;
};

export default GroupsAndTeamsWrapper;
